import dayjs from "dayjs";
import { PlusIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import type { AuthProvider } from "../../types";
import { usePersistedAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TableFilterRow } from "../TableFilterRow";

export function AuthProvidersTable({
  providers,
}: {
  providers: AuthProvider[] | undefined;
}) {
  const { options, filterTextProps } = usePersistedAdminGridOptions(
    "authProviders",
    providers,
    [
      {
        field: "name",
        cellRenderer: (params) => (
          <Link
            href={{
              to: "/admin/auth/providers/$id/edit",
              params: { id: params.id },
            }}
          >
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.id,
          name: params.data.name,
        }),
      },
      {
        field: "description",
        headerName: "Description",
      },
      { field: "type", headerName: "Type" },
      {
        field: "emailDomains",
        headerName: "Domains",
        cellRenderer: (params) =>
          params.data.isDefault ? (
            <Badge size="sm">Default</Badge>
          ) : (
            <Text size="1">{params.value?.join(", ")}</Text>
          ),
      },
      {
        headerName: "Secret Expiry",
        cellRenderer: (params) => <SecretExpiration provider={params.data} />,
      },
    ],
  );

  return (
    <>
      <TableFilterRow inputProps={filterTextProps}>
        <Link
          href={{ to: "/admin/auth/providers/new" }}
          className={buttonVariants()}
        >
          <PlusIcon data-slot="icon" />
          Add provider
        </Link>
      </TableFilterRow>
      <AdminGrid {...options} />
    </>
  );
}

function SecretExpiration({ provider }: { provider: AuthProvider }) {
  const expiration = provider.authProviderConfig.find(
    (p) => p.name === "secret_expiration",
  )?.value;
  const fromNow = expiration ? dayjs(expiration).diff(dayjs(), "days") : null;

  return fromNow ? (
    fromNow > 0 ? (
      <Text
        className={
          fromNow > 90
            ? "text-green-600"
            : fromNow > 30
              ? "text-yellow-600"
              : "text-red-600"
        }
      >
        {fromNow} days
      </Text>
    ) : (
      <Badge color="red">Expired</Badge>
    )
  ) : (
    <Text muted size="2">
      No expiration
    </Text>
  );
}
